import type { ScientificObjectRef } from '../../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../../scientific-objects/ScientificObjectSummary';
import type { Peptide } from './Peptide';
import { getPeptide } from './peptideApi';
import { useAsyncResource } from '../../../hooks/useAsyncResource';

interface PeptideWorkspaceProps {
  readonly reference: ScientificObjectRef;
  readonly summary: ScientificObjectSummary;
}

const terminusLabel = (value: Peptide['nTerminus']): string => (value === 'UNMODIFIED' ? 'Unmodified' : value);

export function PeptideWorkspace({ reference, summary }: PeptideWorkspaceProps) {
  const state = useAsyncResource(() => getPeptide(reference.id), [reference.id], 'Unable to load this peptide.');

  return (
    <main className="peptide-workspace" data-object-id={reference.id}>
      <header className="peptide-heading">
        <p className="eyebrow">Peptide</p>
        <h1>{summary.displayName}</h1>
        <p className="peptide-id">{reference.id}</p>
      </header>
      {state.status === 'loading' ? <p>Loading peptide details...</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? <PeptideDetails peptide={state.value} /> : null}
    </main>
  );
}

function PeptideDetails({ peptide }: { readonly peptide: Peptide }) {
  return (
    <div className="peptide-details">
      <section aria-labelledby="peptide-overview-heading">
        <h2 id="peptide-overview-heading">Overview</h2>
        <dl className="peptide-facts">
          <div>
            <dt>Preferred name</dt>
            <dd>{peptide.preferredName}</dd>
          </div>
          <div>
            <dt>Length</dt>
            <dd>{peptide.length} residues</dd>
          </div>
          <div>
            <dt>N terminus</dt>
            <dd>{terminusLabel(peptide.nTerminus)}</dd>
          </div>
          <div>
            <dt>C terminus</dt>
            <dd>{terminusLabel(peptide.cTerminus)}</dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="peptide-sequences-heading">
        <h2 id="peptide-sequences-heading">Sequence representations</h2>
        <dl className="peptide-sequences">
          <div>
            <dt>One-letter sequence</dt>
            <dd>
              <code>{peptide.oneLetterSequence}</code>
            </dd>
          </div>
          <div>
            <dt>Three-letter sequence</dt>
            <dd>
              <code>{peptide.threeLetterSequence}</code>
            </dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="peptide-residues-heading">
        <h2 id="peptide-residues-heading">Ordered residues</h2>
        <table className="peptide-residues">
          <thead>
            <tr>
              <th scope="col">Position</th>
              <th scope="col">Residue</th>
              <th scope="col">Code</th>
            </tr>
          </thead>
          <tbody>
            {peptide.residues.map((residue) => (
              <tr key={residue.ordinal}>
                <td>{residue.ordinal}</td>
                <td>{residue.preferredName}</td>
                <td>
                  {residue.threeLetterCode} ({residue.oneLetterCode})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
